import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

export type AuthPanelItem = {
  title: string;
  body: string;
  genieIcon: GenieIconName;
};

export type AuthPanelProof = {
  stat: string;
  tagline: string;
};

export type AuthVariant = {
  id: string;
  headline: string;
  sub: string;
  layout: "cards" | "proof";
  items?: AuthPanelItem[];
  proof?: AuthPanelProof;
};

export const signInVariants: AuthVariant[] = [
  {
    id: "signin-busy",
    headline: "Your Genie's been busy.",
    sub: "Step back in and see who you helped.",
    layout: "cards",
    items: [
      {
        title: "Conversations answered",
        body: "Every question handled, transcribed and tagged",
        genieIcon: "chat-check",
      },
      {
        title: "Customers looked after",
        body: "Questions resolved while you slept",
        genieIcon: "smile",
      },
      {
        title: "What people are asking",
        body: "Trends, top questions and gaps to fill",
        genieIcon: "trending-up",
      },
    ],
  },
  {
    id: "signin-talking",
    headline: "Now we're talking.",
    sub: "Pick up where your Genie left off.",
    layout: "cards",
    items: [
      {
        title: "24/7 voice support",
        body: "Your Genie never sleeps, never misses a call",
        genieIcon: "microphone",
      },
      {
        title: "Real answers, real voices",
        body: "Customers helped in their own words",
        genieIcon: "chat",
      },
      {
        title: "One inbox, every channel",
        body: "Voice, chat, QR and embed — all in one view",
        genieIcon: "dashboard",
      },
    ],
  },
  {
    id: "signin-nightshift",
    headline: "Back to the team that doesn't clock off.",
    sub: "Your Genie handled the night shift. Here's the recap.",
    layout: "proof",
    proof: {
      stat: "127 conversations · 41 questions answered · 6 callbacks queued — last 24h",
      tagline: "Now we're talking.",
    },
  },
];

export const signUpVariants: AuthVariant[] = [
  {
    id: "signup-outcome",
    headline: "Give every customer a voice that always answers.",
    sub: "Build your first support Genie in under 5 minutes — no code, no call centre.",
    layout: "cards",
    items: [
      {
        title: "Sounds like your brand",
        body: "Voice and tone you control",
        genieIcon: "microphone",
      },
      {
        title: "Knows your business cold",
        body: "Pulls from docs, sites, drives and PDFs",
        genieIcon: "book-open",
      },
      {
        title: "Helps people, not scripts",
        body: "Real answers in real conversations",
        genieIcon: "chat",
      },
      {
        title: "Live anywhere instantly",
        body: "Link, QR, embed or phone",
        genieIcon: "share",
      },
    ],
  },
  {
    id: "signup-steps",
    headline: "From idea to live support in 5 minutes.",
    sub: "Three steps. No engineers. Helping customers by lunchtime.",
    layout: "cards",
    items: [
      {
        title: "1. Train it",
        body: "Drop in docs, links and FAQs",
        genieIcon: "book-open",
      },
      {
        title: "2. Voice it",
        body: "Pick a voice, set the personality",
        genieIcon: "microphone",
      },
      {
        title: "3. Share it",
        body: "Link, QR, embed or phone",
        genieIcon: "share",
      },
    ],
  },
  {
    id: "signup-bold",
    headline: "Your best support rep. Cloned, on-brand, always on.",
    sub: "Start free. Launch your first Genie in 5 minutes.",
    layout: "cards",
    items: [
      {
        title: "Trained on your business",
        body: "Your knowledge, not generic AI",
        genieIcon: "brain",
      },
      {
        title: "Speaks like your team",
        body: "Custom voice and personality",
        genieIcon: "microphone",
      },
      {
        title: "Answers anywhere, anytime",
        body: "Voice and chat that never sleep",
        genieIcon: "chat-check",
      },
      {
        title: "Live everywhere",
        body: "Web, voice, QR and embed",
        genieIcon: "share",
      },
    ],
  },
];

export const pickRandomVariant = (variants: AuthVariant[]): AuthVariant =>
  variants[Math.floor(Math.random() * variants.length)];
